import { Link } from "react-router-dom";
import { Cake, CalendarCheck, HeartHandshake, MapPin, Users } from "lucide-react";
import { Helmet } from "@dr.pogodin/react-helmet";
import { Logo } from "@/components/lembra/Logo";
import { PageMeta } from "@/components/lembra/PageMeta";
import { SkipToContent } from "@/components/lembra/SkipToContent";
// MS50: session-aware CTAs so authed visitors don't get sent to
// /signup (which then bounces them since they already have an
// account). Authed users go straight to /chat to start a brief.
import { useAuth } from "@/hooks/useAuth";

/**
 * About page, MS5 (Phase 2 of the post-landing micro-sprint loop).
 *
 * Heidi doesn't have a literal About page (their /about-us hover
 * resolves to a sub-menu of Safety / Trust / FAQs), so this rewrite
 * applies the Heidi DESIGN SYSTEM patterns established in MS1–MS3
 * rather than copying a literal Heidi page:
 *   • Eyebrow pill (JetBrains Mono small caps)
 *   • Serif H1 / H2 (Newsreader 500) with italic-emphasis on the
 *     operative word in coral
 *   • DM Sans lede + body in muted navy
 *   • Cream → butter gradient band on the hero, plain cream below
 *
 * Also a copy refresh, the previous draft was written in the
 * cake-first era and re-narrowed Lembra to bakeries in several
 * sections ("Why local bakeries matter to us", feature cards
 * about cakes-from-bakeries-only, etc.). Updated to reflect the
 * multi-vendor concierge positioning the rest of the site now uses.
 *
 * Brand voice notes (kept from prior file):
 * - Lembra is the brand, never "she."
 * - No em-dashes in body copy. Commas, full stops, parentheses,
 *   or line breaks instead.
 */

const monoFamily =
  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const displayFamily = "Newsreader, Georgia, serif";
const sansFamily = "DM Sans, sans-serif";

const About = () => {
  // MS50: session-aware CTA destinations (see useAuth import note).
  const { user, session, loading: authLoading } = useAuth();
  const isAuthed = !authLoading && !!session && !!user;
  const primaryCtaTo = isAuthed ? "/chat" : "/signup";
  const primaryCtaLabel = isAuthed ? "Start a celebration" : "Create an account";
  return (
  <>
    <PageMeta
      title="About Lembra | Workplace celebrations, made in Australia"
      description="Lembra is an Australian workplace celebration concierge. Cakes, hampers, flowers, vouchers, workshops, and spa experiences from independent local makers, picked and delivered for every birthday, work anniversary, welcome, and farewell."
      path="/about"
    />
    <Helmet>
      {/* MS188: BreadcrumbList for "Lembra › About". Closes the
          trail consistent with MS181/MS182 pattern. AboutPage is
          omitted — the index.html @id="#organization" + #localbusiness
          already cover Lembra-the-entity; an extra AboutPage here
          would duplicate without adding crawler signal. */}
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Lembra", item: "https://lembra.com.au" },
          { "@type": "ListItem", position: 2, name: "About", item: "https://lembra.com.au/about" },
        ],
      })}</script>
    </Helmet>
    <SkipToContent />
    <div className="min-h-screen bg-cream flex flex-col">
      <header className="py-6 px-6 md:px-10">
        <Logo size="md" />
      </header>

      <main id="main" className="flex-1 w-full">
        {/* ── Hero band ───────────────────────────────────────────
            Cream → butter gradient with the Heidi-pattern editorial
            framing (eyebrow pill + serif H1 italic-emphasis + DM
            Sans lede). */}
        <section
          className="px-6 md:px-10"
          style={{
            background:
              "linear-gradient(180deg, #FBF3D0 0%, #F7E8A4 55%, #FBF3D0 100%)",
            padding: "clamp(56px, 7vw, 96px) 0 clamp(48px, 6vw, 80px)",
          }}
        >
          <div
            className="text-center"
            style={{ maxWidth: 880, margin: "0 auto" }}
          >
            <span
              className="inline-flex items-center rounded-full px-3 py-1"
              style={{
                background: "rgba(29,52,97,0.06)",
                border: "1px solid rgba(29,52,97,0.08)",
                fontFamily: monoFamily,
                fontSize: 10,
                fontWeight: 500,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "rgba(29,52,97,0.7)",
              }}
            >
              About Lembra
            </span>

            <h1
              style={{
                fontFamily: displayFamily,
                fontWeight: 500,
                fontSize: "clamp(2.2rem, 4.6vw, 3.6rem)",
                lineHeight: 1.05,
                letterSpacing: "-0.02em",
                color: "#1D3461",
                margin: "20px 0 18px",
                maxWidth: "20ch",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            >
              Built so every milestone feels{" "}
              <em
                style={{
                  fontStyle: "italic",
                  fontWeight: 500,
                  color: "#C4564A",
                }}
              >
                seen
              </em>
              .
            </h1>

            <p
              style={{
                fontFamily: sansFamily,
                fontSize: "clamp(1rem, 1.5vw, 1.15rem)",
                lineHeight: 1.55,
                color: "rgba(45,41,38,0.72)",
                margin: "0 auto",
                maxWidth: "52ch",
              }}
            >
              Lembra is a team of people who got tired of watching good
              workplaces quietly lose their culture one missed birthday at a
              time. So we built the concierge we wished existed.
            </p>
          </div>
        </section>

        {/* ── Body sections ─────────────────────────────────────── */}
        <div
          className="mx-auto px-6 md:px-10 pb-20"
          style={{ maxWidth: 760, width: "100%" }}
        >
          {/* The problem */}
          <section
            className="border-b"
            style={{
              borderColor: "rgba(29,52,97,0.08)",
              padding: "clamp(48px, 6vw, 72px) 0",
            }}
          >
            <h2
              style={{
                fontFamily: displayFamily,
                fontWeight: 500,
                fontSize: "clamp(1.6rem, 2.8vw, 2.1rem)",
                lineHeight: 1.12,
                letterSpacing: "-0.015em",
                color: "#1D3461",
                margin: "0 0 18px",
                maxWidth: "24ch",
              }}
            >
              The problem nobody puts on a{" "}
              <em
                style={{
                  fontStyle: "italic",
                  fontWeight: 500,
                  color: "#C4564A",
                }}
              >
                roadmap
              </em>
              .
            </h2>
            <div
              className="space-y-4"
              style={{
                fontFamily: sansFamily,
                fontSize: "1.0625rem",
                lineHeight: 1.65,
                color: "rgba(45,41,38,0.78)",
              }}
            >
              <p>
                Every growing company in Australia has the same quiet failure
                mode. A team member's birthday passes without a word. A work
                anniversary slips by. The office manager, usually the same
                person already running a dozen other unowned things, spends an
                hour before every celebration hunting down a vendor with a free
                slot, chasing signatures on a card, and checking who's coeliac.
              </p>
              <p>
                Some people get celebrated. Others get overlooked. Hybrid team
                members in particular almost never make the list. And when
                dietary requirements fail, the recognition backfires. A
                non-halal hamper. A gluten-free cake forgotten. A bouquet
                arriving when the recipient is at the regional office.
              </p>
              <p>
                It's a culture tax paid in forgotten moments. For a lot of the
                teams we talked to, it was one of the top things they wanted to
                fix but couldn't justify hiring for.
              </p>
            </div>
          </section>

          {/* What we built */}
          <section
            className="border-b"
            style={{
              borderColor: "rgba(29,52,97,0.08)",
              padding: "clamp(48px, 6vw, 72px) 0",
            }}
          >
            <h2
              style={{
                fontFamily: displayFamily,
                fontWeight: 500,
                fontSize: "clamp(1.6rem, 2.8vw, 2.1rem)",
                lineHeight: 1.12,
                letterSpacing: "-0.015em",
                color: "#1D3461",
                margin: "0 0 18px",
                maxWidth: "24ch",
              }}
            >
              What we{" "}
              <em
                style={{
                  fontStyle: "italic",
                  fontWeight: 500,
                  color: "#C4564A",
                }}
              >
                built
              </em>
              .
            </h2>
            <p
              style={{
                fontFamily: sansFamily,
                fontSize: "1.0625rem",
                lineHeight: 1.65,
                color: "rgba(45,41,38,0.78)",
                margin: "0 0 32px",
              }}
            >
              Lembra is a concierge for workplace celebrations. Brief us once
              about the person. We pick from independent Australian makers
             , cakes, hampers, flowers, vouchers, workshops, spa experiences,
              sign the card, book the courier, and deliver on the day. Every
              dietary requirement, every preference, captured once and applied
              forever.
            </p>

            <div className="grid gap-5 md:grid-cols-2">
              <FeatureCard
                icon={CalendarCheck}
                title="Automated from the date up"
                body="Every birthday, anniversary, welcome and farewell pulled from your HR system so nothing falls through the cracks. Remote team members get the same treatment as the ones at the office desk next to the kitchen."
                tint="butter"
              />
              <FeatureCard
                icon={Users}
                title="Dietary-inclusive by default"
                body="Allergies, vegan, halal, kosher, gluten-free, dairy-free. Captured once per person and applied to every order thereafter. No more 'sorry, this one's not for you' moments at a shared table."
                tint="sky"
              />
              <FeatureCard
                icon={Cake}
                title="Local makers, never imported"
                body="Every cake, hamper, bouquet and voucher on the platform comes from an independent Australian business. No generic gift sets, no three-week shipping, no mystery boxes from overseas. Real makers, paid fairly."
                tint="coral"
              />
              <FeatureCard
                icon={HeartHandshake}
                title="Zero admin to run"
                body="Office managers and people leads shouldn't have to choose between doing their actual job and being the person who remembers everyone's birthday. Lembra hands that weight back."
                tint="butter"
              />
            </div>
          </section>

          {/* Why local makers matter */}
          <section
            className="border-b"
            style={{
              borderColor: "rgba(29,52,97,0.08)",
              padding: "clamp(48px, 6vw, 72px) 0",
            }}
          >
            <h2
              style={{
                fontFamily: displayFamily,
                fontWeight: 500,
                fontSize: "clamp(1.6rem, 2.8vw, 2.1rem)",
                lineHeight: 1.12,
                letterSpacing: "-0.015em",
                color: "#1D3461",
                margin: "0 0 18px",
                maxWidth: "26ch",
              }}
            >
              Why local makers matter to{" "}
              <em
                style={{
                  fontStyle: "italic",
                  fontWeight: 500,
                  color: "#C4564A",
                }}
              >
                us
              </em>
              .
            </h2>
            <div
              className="space-y-4"
              style={{
                fontFamily: sansFamily,
                fontSize: "1.0625rem",
                lineHeight: 1.65,
                color: "rgba(45,41,38,0.78)",
              }}
            >
              <p>
                When we looked at how corporate celebration spend actually
                flowed in Australia, most of it went either to big
                imported-hamper companies or generic catering at scale.
                Independent bakeries, florists, cheesemongers, coffee
                roasters, the people whose work anyone actually remembers
                receiving, were mostly cut out of the equation because the
                B2B side of their business was too fragmented to chase.
              </p>
              <p>
                That felt wrong to us. So half of what Lembra does is for
                teams, and the other half is for the makers.
              </p>
              <p>
                Every vendor on our platform is an independent small
                business. We give them consistent B2B demand, straightforward
                weekly payment terms, and none of the retail headaches of
                finding new corporate clients one coffee chat at a time. If
                Lembra works, Australia's best independent makers get a more
                reliable revenue base to build on. The gift at your next
                office celebration then comes from somewhere you'd genuinely
                want to visit on a Saturday morning.
              </p>
              <p>
                Every order that goes through the platform is one more reason
                an independent vendor gets to keep doing what they do.
              </p>
            </div>
          </section>

          {/* What we believe */}
          <section
            className="border-b"
            style={{
              borderColor: "rgba(29,52,97,0.08)",
              padding: "clamp(48px, 6vw, 72px) 0",
            }}
          >
            <h2
              style={{
                fontFamily: displayFamily,
                fontWeight: 500,
                fontSize: "clamp(1.6rem, 2.8vw, 2.1rem)",
                lineHeight: 1.12,
                letterSpacing: "-0.015em",
                color: "#1D3461",
                margin: "0 0 24px",
                maxWidth: "20ch",
              }}
            >
              What we{" "}
              <em
                style={{
                  fontStyle: "italic",
                  fontWeight: 500,
                  color: "#C4564A",
                }}
              >
                believe
              </em>
              .
            </h2>
            <ul
              className="space-y-5"
              style={{
                fontFamily: sansFamily,
                fontSize: "1.0625rem",
                lineHeight: 1.65,
                color: "rgba(45,41,38,0.78)",
                listStyle: "none",
                margin: 0,
                padding: 0,
              }}
            >
              {BELIEFS.map((b) => (
                <li key={b.headline} className="flex gap-4">
                  <span
                    aria-hidden="true"
                    style={{
                      flexShrink: 0,
                      width: 6,
                      height: 6,
                      marginTop: 12,
                      borderRadius: 999,
                      background: "#C4564A",
                    }}
                  />
                  <div>
                    <p
                      style={{
                        fontFamily: displayFamily,
                        fontWeight: 500,
                        fontSize: "1.15rem",
                        lineHeight: 1.3,
                        color: "#1D3461",
                        margin: "0 0 4px",
                      }}
                    >
                      {b.headline}
                    </p>
                    <p style={{ margin: 0 }}>{b.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* Where we are */}
          <section
            style={{ padding: "clamp(48px, 6vw, 72px) 0 clamp(32px, 4vw, 56px)" }}
          >
            <div className="flex items-start gap-3 mb-4">
              <MapPin
                className="text-coral mt-1.5"
                size={20}
                strokeWidth={1.8}
              />
              <h2
                style={{
                  fontFamily: displayFamily,
                  fontWeight: 500,
                  fontSize: "clamp(1.6rem, 2.8vw, 2.1rem)",
                  lineHeight: 1.12,
                  letterSpacing: "-0.015em",
                  color: "#1D3461",
                  margin: 0,
                }}
              >
                Built in{" "}
                <em
                  style={{
                    fontStyle: "italic",
                    fontWeight: 500,
                    color: "#C4564A",
                  }}
                >
                  Melbourne
                </em>
                , made for Australian workplaces.
              </h2>
            </div>
            <p
              style={{
                fontFamily: sansFamily,
                fontSize: "1.0625rem",
                lineHeight: 1.65,
                color: "rgba(45,41,38,0.78)",
                margin: 0,
                paddingLeft: 32,
              }}
            >
              Lembra is based in South Yarra and built for Australian
              workplaces, with a growing network of independent local
              makers. Every integration we build is with tools Australian
              companies actually use (Employment Hero, BambooHR, Xero), and
              every product is sourced from vendors who fit the brief —
              the company, the recipient, the occasion.
            </p>
          </section>

          {/* CTA, navy bookend matching FinalCTA on the landing */}
          <section style={{ padding: "0 0 16px" }}>
            <div
              className="rounded-3xl text-center px-8 py-10 md:px-12 md:py-12"
              style={{
                background: "#1D3461",
                color: "#FAF6F0",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Decorative radial glows, same motif as FinalCTA. */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "radial-gradient(ellipse 380px 160px at 80% 0%, rgba(247,232,164,0.08), transparent 60%), radial-gradient(ellipse 320px 200px at 10% 100%, rgba(168,197,226,0.06), transparent 60%)",
                }}
              />
              <div className="relative">
                <h2
                  style={{
                    fontFamily: displayFamily,
                    fontWeight: 500,
                    fontSize: "clamp(1.7rem, 3.2vw, 2.4rem)",
                    lineHeight: 1.1,
                    letterSpacing: "-0.018em",
                    color: "#FAF6F0",
                    margin: "0 0 14px",
                  }}
                >
                  Try it with your{" "}
                  <em
                    style={{
                      fontStyle: "italic",
                      fontWeight: 500,
                      color: "#F7E8A4",
                    }}
                  >
                    team
                  </em>
                  .
                </h2>
                <p
                  style={{
                    fontFamily: sansFamily,
                    fontSize: "0.95rem",
                    lineHeight: 1.55,
                    color: "rgba(250,246,240,0.72)",
                    margin: "0 auto 28px",
                    maxWidth: "44ch",
                  }}
                >
                  Upload your people. Point us at a few dates. We'll take it
                  from there.
                </p>
                <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
                  {/* MS50: authed visitors skip /signup (which would
                      bounce them) and head straight to the concierge
                      to start their first/next celebration. */}
                  <Link
                    to={primaryCtaTo}
                    className="inline-flex items-center justify-center rounded-full bg-coral px-7 py-3.5 text-sm font-medium text-white transition-all hover:bg-coral/90 hover:-translate-y-px focus:outline-none focus:ring-2 focus:ring-coral focus:ring-offset-2 focus:ring-offset-navy"
                    style={{
                      fontFamily: sansFamily,
                      boxShadow: "0 4px 12px -4px rgba(196,86,74,0.4)",
                    }}
                  >
                    {primaryCtaLabel}
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center rounded-full px-7 py-3.5 text-sm font-medium text-cream transition-all hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-cream/60"
                    style={{
                      fontFamily: sansFamily,
                      border: "1.5px solid rgba(250,246,240,0.3)",
                    }}
                  >
                    Talk to us
                  </Link>
                </div>
              </div>
            </div>
          </section>

          <div className="pt-6">
            <Link
              to="/"
              className="text-sm text-coral hover:underline"
              style={{ fontFamily: sansFamily }}
            >
              ← Back to home
            </Link>
          </div>
        </div>
      </main>
    </div>
  </>
  );
};

/* ─── Sub-components ────────────────────────────────────────────── */

interface FeatureCardProps {
  icon: typeof CalendarCheck;
  title: string;
  body: string;
  tint: "butter" | "sky" | "coral";
}

function FeatureCard({ icon: Icon, title, body, tint }: FeatureCardProps) {
  const tintBg =
    tint === "butter"
      ? "rgba(247,232,164,0.5)"
      : tint === "sky"
        ? "rgba(168,197,226,0.3)"
        : "rgba(196,86,74,0.18)";
  return (
    <div
      className="rounded-2xl bg-white p-6"
      style={{
        border: "1px solid rgba(29,52,97,0.06)",
        boxShadow: "0 1px 2px rgba(29,52,97,0.04)",
      }}
    >
      <div
        className="flex items-center justify-center h-10 w-10 rounded-xl mb-4"
        style={{ background: tintBg }}
      >
        <Icon className="h-5 w-5 text-navy" strokeWidth={1.8} />
      </div>
      <h3
        style={{
          fontFamily: displayFamily,
          fontWeight: 500,
          fontSize: "1.15rem",
          lineHeight: 1.25,
          color: "#1D3461",
          margin: "0 0 8px",
        }}
      >
        {title}
      </h3>
      <p
        style={{
          fontFamily: sansFamily,
          fontSize: "0.9375rem",
          lineHeight: 1.55,
          color: "rgba(45,41,38,0.7)",
          margin: 0,
        }}
      >
        {body}
      </p>
    </div>
  );
}

const BELIEFS = [
  {
    headline: "Recognition should be automatic, not heroic.",
    body:
      "If celebrating someone's birthday depends on any one person remembering, it will fail eventually.",
  },
  {
    headline: "Inclusion isn't a nice-to-have.",
    body:
      "A celebration that someone on your team can't actually eat isn't a celebration for them, and everyone at the table notices.",
  },
  {
    headline: "Local beats imported.",
    body:
      "The best thing we can do with the budget a company has set aside for culture is channel it toward the small Australian businesses that make the product memorable in the first place.",
  },
  {
    headline: "Good software should disappear.",
    body:
      "If Lembra is doing its job right, nobody on your team should ever have to think about it. Cakes just start showing up. Cards just arrive signed. Everyone feels a bit more seen.",
  },
];

export default About;
